import type { ReactNode, RefObject } from 'react';

import { cn } from '@nesso/ui/lib/utils';
import { Button } from '@nesso/ui/primitives/button';
import { DropdownMenuItem } from '@nesso/ui/primitives/dropdown-menu';

import { HighlightMatch } from '@/shared/ui/text/highlight-match';

const ROW_PADDING = 'p-2';

type RowMode = 'menu-item' | 'list-item';

type Props<T> = {
    hasData: boolean;
    isError: boolean;
    errorMessage?: string;
    items: T[];
    getKey: (item: T) => string;
    getLabel: (item: T) => string;
    isItemDisabled?: (item: T) => boolean;
    renderItemSuffix?: (item: T) => ReactNode;
    searchTerm: string;
    highlightedIndex: number;
    optionIdPrefix: string;
    onSelect: (item: T) => void;
    onMouseEnter: (index: number) => void;
    itemRefs: RefObject<Array<HTMLElement | null>>;
    emptyAction?: { label: string; onAction: () => void };
    as: RowMode;
};

export function SearchComboboxRows<T>({
    hasData,
    isError,
    errorMessage,
    items,
    getKey,
    getLabel,
    isItemDisabled,
    renderItemSuffix,
    searchTerm,
    highlightedIndex,
    optionIdPrefix,
    onSelect,
    onMouseEnter,
    itemRefs,
    emptyAction,
    as,
}: Props<T>) {
    if (!hasData && !isError) {
        return (
            <div className={cn(ROW_PADDING, 'font-medium text-base text-muted-foreground')}>
                Loading...
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div className={cn(ROW_PADDING, 'font-medium text-base text-muted-foreground')}>
                {isError && errorMessage ? (
                    errorMessage
                ) : (
                    <>
                        No results found.{' '}
                        {emptyAction && (
                            <Button
                                variant="link"
                                onClick={emptyAction.onAction}
                                className="h-auto p-0 text-base underline cursor-pointer inline text-muted-foreground"
                            >
                                {emptyAction.label}
                            </Button>
                        )}
                    </>
                )}
            </div>
        );
    }

    return (
        <>
            {items.map((item, index) => (
                <SearchComboboxRow
                    key={getKey(item)}
                    item={item}
                    label={getLabel(item)}
                    suffix={renderItemSuffix?.(item)}
                    disabled={isItemDisabled?.(item) ?? false}
                    index={index}
                    searchTerm={searchTerm.trim()}
                    optionId={`${optionIdPrefix}-option-${index}`}
                    isHighlighted={index === highlightedIndex}
                    onSelect={onSelect}
                    onMouseEnter={onMouseEnter}
                    itemRef={(element) => {
                        itemRefs.current[index] = element;
                    }}
                    as={as}
                />
            ))}
        </>
    );
}

type RowProps<T> = {
    item: T;
    label: string;
    suffix: ReactNode;
    disabled: boolean;
    index: number;
    searchTerm: string;
    optionId: string;
    isHighlighted: boolean;
    onSelect: (item: T) => void;
    onMouseEnter: (index: number) => void;
    itemRef: (element: HTMLElement | null) => void;
    as: RowMode;
};

function SearchComboboxRow<T>({
    item,
    label,
    suffix,
    disabled,
    index,
    searchTerm,
    optionId,
    isHighlighted,
    onSelect,
    onMouseEnter,
    itemRef,
    as,
}: RowProps<T>) {
    const content = (
        <div className="font-medium text-popover-foreground w-full flex justify-between items-center">
            <span className="w-full truncate">
                <HighlightMatch text={label} query={searchTerm} />
                {suffix}
            </span>
        </div>
    );

    if (as === 'list-item') {
        return (
            <div
                ref={itemRef}
                id={optionId}
                role="option"
                aria-selected={isHighlighted}
                aria-disabled={disabled || undefined}
                tabIndex={-1}
                onClick={disabled ? undefined : () => onSelect(item)}
                onKeyDown={(event) => {
                    if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
                        event.preventDefault();
                        onSelect(item);
                    }
                }}
                onMouseEnter={disabled ? undefined : () => onMouseEnter(index)}
                className={cn('group cursor-default', ROW_PADDING, disabled && 'opacity-50')}
            >
                {content}
            </div>
        );
    }

    return (
        <DropdownMenuItem
            ref={itemRef}
            id={optionId}
            disabled={disabled}
            onSelect={(event) => {
                event.preventDefault();
                onSelect(item);
            }}
            onMouseEnter={disabled ? undefined : () => onMouseEnter(index)}
            tabIndex={-1}
            className={cn('group cursor-default', ROW_PADDING)}
        >
            {content}
        </DropdownMenuItem>
    );
}
